// CONSTRUIRE UNE ÉTUDE, EN PLEINE PAGE (055).
//
//   /projet/:id/etudes/nouvelle            — une nouvelle étude, du type aux invitations
//   /projet/:id/etudes/nouvelle/entretien  — la même, type « entretiens » déjà choisi
//   /projet/:id/etudes/:studyId/inviter    — inviter des participants à une étude existante
//   /projet/:id/entretiens/nouveau         — « Nouvel entretien » : dans quelle étude ?
//
// Le panneau de droite était trop étroit pour choisir des participants et des
// créneaux : la construction d'une étude a désormais sa page.
import { useState } from 'react';
import { useStudies, useStudy } from '../api/queries';
import type { StudyKind } from '../api/types';
import { Button, EmptyState, ErrorBanner, Eyebrow, Spinner } from '../components/ui';
import { InterviewForm } from '../project/InterviewsScreen';
import { Link, useRouter } from '../router';
import { useCanWrite } from '../write/useCanWrite';
import { WriteAction } from '../write/Forms';
import { KIND_BY_KEY, studyKind } from './kinds';
import { NewStudyWizard } from './NewStudyWizard';
import './studies.css';

const studiesPath = (projectId: string) => `/projet/${encodeURIComponent(projectId)}/etudes`;

function ReadOnly({ projectId }: { projectId: string }) {
  return (
    <main className="screen">
      <Link to={studiesPath(projectId)} className="zy-retour">← Études</Link>
      <EmptyState
        title="Réservé aux responsables du projet"
        description="Créer une étude et inviter des participants est réservé aux responsables du projet et aux administrateurs."
      />
    </main>
  );
}

export function NewStudyPage({ projectId, presetKind = null }: { projectId: string; presetKind?: StudyKind | null }) {
  const canWrite = useCanWrite(projectId);
  const { navigate } = useRouter();
  if (!canWrite) return <ReadOnly projectId={projectId} />;
  return (
    <main className="screen st-builder">
      <Link to={studiesPath(projectId)} className="zy-retour">← Études</Link>
      <div className="zy-page-head__texte">
        <Eyebrow>Nouvelle étude</Eyebrow>
        <h1 className="t-h1">{presetKind ? `Nouvelle étude — ${KIND_BY_KEY[presetKind].label.toLowerCase()}` : 'Construire une étude'}</h1>
        <p className="zy-intro">
          Le type, le cadrage, le protocole ; puis, s’il y a des séances, les participants à inviter, vos créneaux
          et les formats proposés. Chaque personne réserve elle-même son créneau.
        </p>
      </div>
      <NewStudyWizard projectId={projectId} presetKind={presetKind} onDone={() => undefined} />
      <p className="zy-texte-muet">
        <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => navigate(studiesPath(projectId))}>Abandonner</Button>
      </p>
    </main>
  );
}

export function InviteToStudyPage({ projectId, studyId }: { projectId: string; studyId: string }) {
  const canWrite = useCanWrite(projectId);
  const study = useStudy(studyId);
  const back = `${studiesPath(projectId)}/${encodeURIComponent(studyId)}`;
  if (!canWrite) return <ReadOnly projectId={projectId} />;
  if (study.isPending) return <main className="screen"><Spinner label="Chargement de l’étude" /></main>;
  if (study.isError || !study.data) {
    return (
      <main className="screen">
        <Link to={studiesPath(projectId)} className="zy-retour">← Études</Link>
        <ErrorBanner>Étude introuvable dans votre périmètre.</ErrorBanner>
      </main>
    );
  }
  const s = study.data;
  return (
    <main className="screen st-builder">
      <Link to={back} className="zy-retour">← {s.code ?? 'L’étude'}</Link>
      <div className="zy-page-head__texte">
        <Eyebrow>Inviter des participants</Eyebrow>
        <h1 className="t-h1">{s.objective ?? 'Étude sans objectif renseigné'}</h1>
        <p className="zy-intro">Cochez les personnes, vos créneaux et les formats : chacune réserve elle-même son créneau.</p>
      </div>
      <NewStudyWizard
        projectId={projectId}
        existing={{ id: s.id, type: s.type ?? '', objective: s.objective ?? null }}
        onDone={() => undefined}
      />
    </main>
  );
}

/** « Nouvel entretien » : dans une étude existante, ou une nouvelle étude d'entretiens. */
export function NewInterviewPage({ projectId }: { projectId: string }) {
  const canWrite = useCanWrite(projectId);
  const studies = useStudies(projectId);
  const { navigate } = useRouter();
  const [filter, setFilter] = useState('');
  if (!canWrite) return <ReadOnly projectId={projectId} />;
  const withSessions = (studies.data ?? []).filter((s) => {
    const k = studyKind(s.type);
    return k !== 'audit' && k !== 'questionnaire' && !s.is_baseline;
  });
  const shown = withSessions.filter((s) =>
    !filter.trim() || `${s.code ?? ''} ${s.objective ?? ''}`.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <main className="screen st-builder">
      <Link to={`/projet/${encodeURIComponent(projectId)}/entretiens`} className="zy-retour">← Entretiens</Link>
      <div className="zy-page-head__texte">
        <Eyebrow>Nouvel entretien</Eyebrow>
        <h1 className="t-h1">Organiser des entretiens</h1>
        <p className="zy-intro">
          Un entretien appartient à une étude. Invitez des participants dans une étude existante, ou construisez une
          nouvelle étude d’entretiens.
        </p>
        <div className="screen-actions">
          <Button variant="accent" onClick={() => navigate(`${studiesPath(projectId)}/nouvelle/entretien`)}>
            Nouvelle étude d’entretiens
          </Button>
          <WriteAction label="Saisir un entretien déjà mené" title="Saisir un entretien déjà mené" description="Qui, quand, dans quelle étude. L’enregistrement s’envoie ensuite.">
            {(close) => <InterviewForm projectId={projectId} onDone={close} />}
          </WriteAction>
        </div>
      </div>

      {studies.isPending ? <Spinner label="Chargement des études" /> : null}
      {withSessions.length > 0 ? (
        <section className="test-block">
          <h2 className="t-h3">Dans une étude existante</h2>
          {withSessions.length > 6 ? (
            <input className="zy-input" type="search" placeholder="Rechercher une étude…" aria-label="Rechercher une étude"
              value={filter} onChange={(e) => setFilter(e.target.value)} />
          ) : null}
          <ul className="st-pick-list">
            {shown.map((s) => (
              <li key={s.id}>
                <span>
                  {s.code ? <><strong>{s.code}</strong> · </> : null}{s.type}
                  <span className="inv-sub">{s.objective ?? 'Sans objectif renseigné'}</span>
                </span>
                <Button variant="ghost" onClick={() => navigate(`${studiesPath(projectId)}/${encodeURIComponent(s.id)}/inviter`)}>
                  Inviter des participants
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </main>
  );
}
